import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

export const EASE = [0.22, 1, 0.36, 1];

export function MaskLines({ lines, className = "", as: Tag = "h1", delay = 0.1 }) {
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className="block will-change-transform"
            style={{ fontSize: "28px" }}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

export function Reveal({ children, className = "", delay = 0, y = 28 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function Marquee({ items, className = "" }) {
  const row = [...items, ...items];
  return (
    <div className={`overflow-hidden border-y border-amber/20 bg-coal/60 ${className}`} data-testid="keyword-marquee">
      <div className="marquee-track py-4">
        {row.map((item, i) => (
          <span key={i} className="flex items-center shrink-0">
            <span className="font-display font-bold uppercase tracking-[0.14em] text-lg text-white/70 px-6 whitespace-nowrap">
              {item}
            </span>
            <span className="w-2 h-2 bg-amber rotate-45 shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function ChapterTag({ num, title }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <span className="font-mono text-xs text-amber tracking-widest">{num}</span>
      <span className="h-px flex-1 max-w-[64px] bg-amber/50" />
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">{title}</span>
    </div>
  );
}

export function SpotlightCard({ children, className = "", testId }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div data-testid={testId} onMouseMove={onMove} className={`spotlight corner-card bg-card border border-white/10 ${className}`}>
      {children}
    </div>
  );
}

export function FaqAccordion({ faqs, testIdPrefix = "faq" }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-list`}>
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className={`border bg-card transition-colors duration-300 ${isOpen ? "border-amber/50" : "border-white/10"}`}>
            <button
              data-testid={`${testIdPrefix}-question-${i}`}
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4"
            >
              <span className="font-display font-bold uppercase tracking-wide text-base sm:text-lg text-white">{f.q}</span>
              <ChevronDown className={`w-5 h-5 shrink-0 text-amber transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              <div className="overflow-hidden">
                <p data-testid={`${testIdPrefix}-answer-${i}`} className="px-5 sm:px-6 pb-5 text-sm sm:text-base text-gray-400 leading-relaxed">
                  {f.a}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
